import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CdkMenuModule } from '@angular/cdk/menu';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TooltipModule } from 'primeng/tooltip';
import { SectionService } from '../../../../services/builder/section.service';
import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { SubSectionEditComponent } from '../sub-section/sub-section.component';
import { DialogModule } from 'primeng/dialog';
import { SectionCustomizationPanelComponent } from './section-customization-panel.component';
import { DragDropService } from '../../../../services/builder/drag-drop.service';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'lib-edit-section',
  templateUrl: './section.component.html',
  imports: [
    FormsModule,
    MatButtonModule,
    DragDropModule,
    MatIconModule,
    CdkMenuModule,
    TooltipModule,
    DialogModule,
    SubSectionEditComponent,
    SectionCustomizationPanelComponent,
    MatMenuModule,
    ReactiveFormsModule,
  ],
  providers: [SectionService],
  styles: [
    `
      .columnContainer:empty:not(:focus):before {
        content: attr(data-text);
        align-content: center;
      }
    `,
  ],
})
export class SectionEditComponent {
  protected sectionService = inject(SectionService);
  protected dragdropService = inject(DragDropService);

  sectionId = input.required<string>();
  ref = input.required<HTMLDivElement>();

  inlineTitleEditor = signal(false);
  inlineTitleEditorField =
    viewChild<ElementRef<HTMLInputElement>>('titleInput');
  title = new FormControl('');

  inlineSubTitleEditor = signal(false);
  inlineSubTitleEditorField =
    viewChild<ElementRef<HTMLInputElement>>('subTitleInput');
  subTitle = new FormControl('');

  inlinePointsEditor = signal(false);
  inlinePointsEditorField =
    viewChild<ElementRef<HTMLInputElement>>('pointsInput');
  points = new FormControl(0);

  positions = [
    new ConnectionPositionPair(
      { originX: 'start', originY: 'center' },
      { overlayX: 'start', overlayY: 'center' },
      30,
      0,
    ),
  ];

  constructor() {
    const ref = effect(() => {
      this.sectionService.uniqueId = this.sectionId();
      ref.destroy();
    });

    const ref1 = effect(() => {
      const section = this.sectionService.section();
      if (!section) return;

      this.title.setValue(section.customization.title || '');
      this.subTitle.setValue(section.customization.subTitle || '');
      this.points.setValue(section.customization.points || 0);
      ref1.destroy();
    });

    effect(() => {
      const inlineTitleEditorField = this.inlineTitleEditorField();
      if (!inlineTitleEditorField) return;

      inlineTitleEditorField.nativeElement.focus();
      inlineTitleEditorField.nativeElement.select();
    });

    effect(() => {
      const inlineSubTitleEditorField = this.inlineSubTitleEditorField();
      if (!inlineSubTitleEditorField) return;

      inlineSubTitleEditorField.nativeElement.focus();
      inlineSubTitleEditorField.nativeElement.select();
    });

    effect(() => {
      const inlinePointsEditorField = this.inlinePointsEditorField();
      if (!inlinePointsEditorField) return;

      inlinePointsEditorField.nativeElement.focus();
      inlinePointsEditorField.nativeElement.select();
    });
  }

  updateVisiblity(visible: boolean) {
    if (!visible) this.sectionService.sectionCustomizationVisible.set(visible);
  }

  drop(event: CdkDragDrop<string[], string, string>) {
    console.log('drop', event);
    if (
      event.previousContainer.id == event.container.id &&
      event.previousIndex == event.currentIndex
    )
      return;
    this.sectionService.moveSubSection(
      event.previousContainer.id,
      event.item.data as string,
      event.currentIndex,
    );
  }

  closeInlineTitleEditor() {
    const customization = this.sectionService.section()?.customization;
    if (!customization) return;

    customization.title = this.title.value || '';
    this.sectionService.updateSection(this.sectionId(), { ...customization });
    this.inlineTitleEditor.set(false);
  }

  closeInlineSubTitleEditor() {
    const customization = this.sectionService.section()?.customization;
    if (!customization) return;

    customization.subTitle = this.subTitle.value || '';
    this.sectionService.updateSection(this.sectionId(), { ...customization });
    this.inlineSubTitleEditor.set(false);
  }

  closeInlinePointsEditor() {
    const customization = this.sectionService.section()?.customization;
    if (!customization) return;

    customization.points = this.points.value || 0;
    this.sectionService.updateSection(this.sectionId(), { ...customization });
    this.inlinePointsEditor.set(false);
  }
}
